import { type FormEvent, useState } from 'react';
import { i18n } from '@wms/shared';
import { api } from '../lib/api.js';
import { Dialog } from './Dialog.js';

const { t } = i18n;

/**
 * Resolve an order whose invoice123 push outcome is unknown (invoicePushState
 * AMBIGUOUS, WMS-35). The accountant checks invoice123 manually, then records
 * either that the invoice WAS created (FOUND — links the external invoice) or
 * that it was NOT (releases the order so it can be pushed again). Both require a
 * short note that lands on the order's audit trail.
 */
export function ReconcileInvoiceDialog({
  orderId,
  onClose,
  onReconciled,
}: {
  orderId: string;
  onClose: () => void;
  onReconciled: () => void;
}) {
  const [outcome, setOutcome] = useState<'FOUND' | 'NOT_CREATED'>('FOUND');
  const [invoiceRef, setInvoiceRef] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceUrl, setInvoiceUrl] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reasonOk = reason.trim().length >= 5;
  const refOk = outcome === 'NOT_CREATED' || invoiceRef.trim().length > 0;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const body =
        outcome === 'FOUND'
          ? {
              outcome,
              invoiceRef: invoiceRef.trim(),
              invoiceNumber: invoiceNumber.trim() || undefined,
              invoiceUrl: invoiceUrl.trim() || undefined,
              reason: reason.trim(),
            }
          : { outcome, reason: reason.trim() };
      await api(`/orders/${orderId}/reconcile-invoice`, { method: 'POST', body });
      onReconciled();
    } catch {
      setError(t('common.error'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog title={t('order.reconcile.title')} onClose={onClose}>
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-3 text-sm">
        <p className="text-xs text-muted-foreground">{t('order.reconcile.hint')}</p>

        <fieldset className="space-y-2">
          <legend className="font-medium">{t('order.reconcile.outcome')}</legend>
          <label className="flex items-start gap-2">
            <input
              type="radio"
              name="outcome"
              checked={outcome === 'FOUND'}
              onChange={() => setOutcome('FOUND')}
              className="mt-1"
            />
            <span>{t('order.reconcile.found')}</span>
          </label>
          <label className="flex items-start gap-2">
            <input
              type="radio"
              name="outcome"
              checked={outcome === 'NOT_CREATED'}
              onChange={() => setOutcome('NOT_CREATED')}
              className="mt-1"
            />
            <span>{t('order.reconcile.not_created')}</span>
          </label>
        </fieldset>

        {outcome === 'FOUND' ? (
          <div className="space-y-3 rounded border border-border p-3">
            <label className="block space-y-1">
              <span className="font-medium">{t('order.reconcile.invoice_ref')}</span>
              <input
                value={invoiceRef}
                onChange={(e) => setInvoiceRef(e.target.value)}
                required
                maxLength={200}
                className="w-full rounded border border-border bg-background px-2 py-1.5 font-mono"
              />
            </label>
            <label className="block space-y-1">
              <span className="font-medium">{t('order.reconcile.invoice_number')}</span>
              <input
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                maxLength={200}
                className="w-full rounded border border-border bg-background px-2 py-1.5"
              />
            </label>
            <label className="block space-y-1">
              <span className="font-medium">{t('order.reconcile.invoice_url')}</span>
              <input
                type="url"
                value={invoiceUrl}
                onChange={(e) => setInvoiceUrl(e.target.value)}
                maxLength={2000}
                placeholder="https://"
                className="w-full rounded border border-border bg-background px-2 py-1.5"
              />
            </label>
          </div>
        ) : null}

        <label className="block space-y-1">
          <span className="font-medium">{t('order.reconcile.reason')}</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            rows={3}
            maxLength={2000}
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          />
        </label>

        {error ? <div className="text-red-600">{error}</div> : null}

        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-border px-3 py-1.5"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={busy || !reasonOk || !refOk}
            className="rounded bg-primary text-primary-foreground px-3 py-1.5 disabled:opacity-50"
          >
            {busy ? t('common.loading') : t('order.reconcile.submit')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
